import { BlogArticle } from '../types';

// Shadowing 跟讀法 → CTA: /module/speaking
const article: BlogArticle = {
  slug: 'shadowing-method-speaking-practice',
  date: '2026-09-06',
  readingMinutes: 4,
  tags: ['口語', 'Speaking', '英文', 'Shadowing'],

  locales: {
    zhHant: {
      title: 'Shadowing 跟讀法：唔使出國都練到流利英文口語',
      excerpt: '口語差唔係因為你冇語言環境，而係你開唔到口。Shadowing 係口譯員都用嘅訓練方法，一個人都練到。',
      keywords: 'shadowing, 跟讀法, 英文口語, speaking practice, 英語會話',
      contentHtml: `
        <p>「我英文睇得明、寫得出，但就係講唔出。」呢個係最多香港學生嘅痛點。問題核心係：<strong>講英文需要嘅唔係知識，係口腔肌肉嘅習慣</strong> —— 而肌肉唯有靠反覆訓練。Shadowing（跟讀法）就係針對呢點嘅訓練。</p>

        <h2>咩係 Shadowing？</h2>
        <p>Shadowing 即係「影子跟讀」：播放英文音頻，<strong>落後講者半秒到一秒，即刻跟住佢講</strong>，模仿語速、語調、連讀同停頓。呢個係專業口譯員嘅基本訓練。</p>

        <h2>點解有效？</h2>
        <ul>
          <li><strong>逼你用英文嘅節奏講嘢</strong>，打破「逐個字諗」嘅習慣</li>
          <li><strong>訓練口腔肌肉</strong>，令發音同連讀變成自然反應</li>
          <li><strong>同時練聽力</strong> —— 聽唔明就跟唔到</li>
        </ul>

        <h2>每日 10 分鐘訓練流程</h2>
        <ol>
          <li><strong>選材（1 分鐘）</strong>：30–60 秒嘅短音頻，語速中等，最好有 transcript</li>
          <li><strong>聽一次（1 分鐘）</strong>：了解內容大意</li>
          <li><strong>跟讀 5–8 次（7 分鐘）</strong>：開始可以睇稿，之後要甩稿</li>
          <li><strong>錄低自己（1 分鐘）</strong>：同原音對比，搵出差異</li>
        </ol>

        <div class="tip-box">
          <strong>💡 貼士：</strong>頭幾日跟唔上係正常嘅。可以先用 0.75x 速度慢速跟讀，熟咗先返返原速。重點係模仿語調，唔係背內容。
        </div>

        <h2>常見問題</h2>
        <table>
          <thead>
            <tr><th>問題</th><th>解決方法</th></tr>
          </thead>
          <tbody>
            <tr><td>完全跟唔上</td><td>換更短更慢嘅材料，或者分段跟</td></tr>
            <tr><td>唔知自己讀成點</td><td>錄音對比，或者用語音辨識檢查</td></tr>
            <tr><td>好快悶</td><td>揀你有興趣嘅題材（訪談、故事、演講）</td></tr>
          </tbody>
        </table>

        <h2>想有系統咁練？</h2>
        <p>StudyMind 嘅口語練習有專門嘅 Shadowing 模式：AI 讀一句、你跟一句，仲會用語音辨識即時話你知讀成點：</p>
        <p><a class="blog-cta-button" href="/module/speaking">🗣️ 免費開始 Shadowing 練習 →</a></p>

        <blockquote>口語唔係學返嚟，係練返嚟。每日 10 分鐘，三個月後你會多謝自己。</blockquote>
      `,
    },

    zhHans: {
      title: 'Shadowing 跟读法：不出国也能练出流利英文口语',
      excerpt: '口语差不是因为你没有语言环境，而是你开不了口。Shadowing 是口译员都在用的训练方法，一个人也能练。',
      keywords: 'shadowing, 跟读法, 英文口语, speaking practice, 英语会话',
      contentHtml: `
        <p>「我英文看得懂、写得出，但就是说不出。」这是最多香港学生的痛点。问题核心是：<strong>说英文需要的不是知识，是口腔肌肉的习惯</strong> —— 而肌肉只有靠反复训练。Shadowing（跟读法）就是针对这点的训练。</p>

        <h2>什么是 Shadowing？</h2>
        <p>Shadowing 即「影子跟读」：播放英文音频，<strong>落后讲者半秒到一秒，立刻跟着他讲</strong>，模仿语速、语调、连读和停顿。这是专业口译员的基本训练。</p>

        <h2>为什么有效？</h2>
        <ul>
          <li><strong>逼你用英文的节奏说话</strong>，打破「逐个字想」的习惯</li>
          <li><strong>训练口腔肌肉</strong>，让发音和连读变成自然反应</li>
          <li><strong>同时练听力</strong> —— 听不懂就跟不到</li>
        </ul>

        <h2>每天 10 分钟训练流程</h2>
        <ol>
          <li><strong>选材（1 分钟）</strong>：30–60 秒的短音频，语速中等，最好有 transcript</li>
          <li><strong>听一次（1 分钟）</strong>：了解内容大意</li>
          <li><strong>跟读 5–8 次（7 分钟）</strong>：开始可以看稿，之后要脱稿</li>
          <li><strong>录下自己（1 分钟）</strong>：和原音对比，找出差异</li>
        </ol>

        <div class="tip-box">
          <strong>💡 提示：</strong>头几天跟不上是正常的。可以先用 0.75x 速度慢速跟读，熟了再回到原速。重点是模仿语调，不是背内容。
        </div>

        <h2>常见问题</h2>
        <table>
          <thead>
            <tr><th>问题</th><th>解决方法</th></tr>
          </thead>
          <tbody>
            <tr><td>完全跟不上</td><td>换更短更慢的材料，或者分段跟</td></tr>
            <tr><td>不知道自己读成怎样</td><td>录音对比，或者用语音识别检查</td></tr>
            <tr><td>很快闷</td><td>选你有兴趣的题材（访谈、故事、演讲）</td></tr>
          </tbody>
        </table>

        <h2>想有系统地练？</h2>
        <p>StudyMind 的口语练习有专门的 Shadowing 模式：AI 读一句、你跟一句，还会用语音识别即时告诉你读得怎样：</p>
        <p><a class="blog-cta-button" href="/module/speaking">🗣️ 免费开始 Shadowing 练习 →</a></p>

        <blockquote>口语不是学回来的，是练回来的。每天 10 分钟，三个月后你会多谢自己。</blockquote>
      `,
    },

    en: {
      title: 'The Shadowing Method: Fluent English Speaking Without Leaving Home',
      excerpt: "Weak speaking isn't about lacking a language environment — it's about not opening your mouth. Shadowing is how interpreters train, and you can do it alone.",
      keywords: 'shadowing, English speaking, speaking practice, pronunciation, fluency',
      contentHtml: `
        <p>"I can read and write English — I just can't speak it." That's the most common frustration among Hong Kong students. The core issue: <strong>speaking isn't knowledge, it's muscle memory</strong> — and muscles only grow through repetition. Shadowing trains exactly that.</p>

        <h2>What is shadowing?</h2>
        <p>Shadowing means playing English audio and <strong>repeating what you hear half a second behind the speaker</strong>, copying their speed, intonation, linking and pauses. It's the foundational drill of professional interpreters.</p>

        <h2>Why it works</h2>
        <ul>
          <li><strong>Forces you into English rhythm</strong>, breaking the word-by-word translation habit</li>
          <li><strong>Trains your mouth muscles</strong> until pronunciation and linking become automatic</li>
          <li><strong>Trains listening too</strong> — you can't repeat what you didn't catch</li>
        </ul>

        <h2>The daily 10-minute routine</h2>
        <ol>
          <li><strong>Pick material (1 min)</strong>: a 30–60 second clip, moderate speed, ideally with a transcript</li>
          <li><strong>Listen once (1 min)</strong>: get the gist</li>
          <li><strong>Shadow 5–8 times (7 min)</strong>: start with the transcript, then drop it</li>
          <li><strong>Record yourself (1 min)</strong>: compare with the original and spot the gaps</li>
        </ol>

        <div class="tip-box">
          <strong>💡 Tip:</strong> Falling behind at first is normal. Drop to 0.75x speed until you're comfortable, then return to full speed. The goal is copying the melody, not memorising the content.
        </div>

        <h2>Common problems</h2>
        <table>
          <thead>
            <tr><th>Problem</th><th>Fix</th></tr>
          </thead>
          <tbody>
            <tr><td>Can't keep up at all</td><td>Use shorter, slower clips, or shadow sentence by sentence</td></tr>
            <tr><td>No idea how I sound</td><td>Record and compare, or check with speech recognition</td></tr>
            <tr><td>Gets boring fast</td><td>Pick topics you enjoy — interviews, stories, talks</td></tr>
          </tbody>
        </table>

        <h2>Want structured practice?</h2>
        <p>StudyMind's speaking module has a dedicated Shadowing mode: the AI reads a line, you repeat it, and speech recognition tells you instantly how you did:</p>
        <p><a class="blog-cta-button" href="/module/speaking">🗣️ Start Shadowing practice for free →</a></p>

        <blockquote>Speaking isn't learned — it's trained. Ten minutes a day, and three months from now you'll thank yourself.</blockquote>
      `,
    },
  },
};

export default article;
